import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, User, CheckCircle, Circle, Lock, Play, Eye,
  Brain, Target, Sparkles, TrendingUp, Award, Users,
  ChevronRight, BarChart3, Lightbulb, Rocket, Clock,
  Star, Zap, RefreshCw, Compass, Heart, MessageCircle,
  Briefcase, GraduationCap, Shield, ArrowRight, Download
} from 'lucide-react';
import useTranslation from '../data/useTranslation';
import LanguageSelector from '../components/LanguageSelector';
import { useCurrency } from '../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Situation labels and colors
const SITUATION_COLORS = {
  etudiant: 'bg-blue-500',
  jeune_diplome: 'bg-indigo-500',
  recherche_emploi: 'bg-amber-500',
  salarie: 'bg-green-500',
  manager: 'bg-teal-500',
  dirigeant: 'bg-slate-500',
  freelance: 'bg-cyan-500',
  entrepreneur: 'bg-purple-500',
  liberal: 'bg-rose-500',
  enseignant: 'bg-yellow-600',
  sante: 'bg-red-500',
  artiste: 'bg-fuchsia-500',
  reconversion: 'bg-orange-500',
  parent_foyer: 'bg-lime-500',
  retraite: 'bg-emerald-500',
  autre: 'bg-gray-500',
};

// Test configuration
const getTestsConfig = (t) => [
  { id: 'disc', name: t.tests.disc.name, subtitle: t.tests.disc.description, icon: '/picto-disc.png', color: '#EF4444', duration: 20 },
  { id: 'bigfive', name: t.tests.bigfive.name, subtitle: t.tests.bigfive.description, icon: '/picto-bigfive.png', color: '#A855F7', duration: 15 },
  { id: 'mbti', name: t.tests.mbti.name, subtitle: t.tests.mbti.description, icon: '/picto-mbti.png', color: '#3B82F6', duration: 10 },
  { id: 'via', name: t.tests.via.name, subtitle: t.tests.via.description, icon: '/picto-via.png', color: '#22C55E', duration: 30 },
  { id: 'schwartz', name: t.tests.schwartz.name, subtitle: t.tests.schwartz.description, icon: 'schwartz', color: '#F59E0B', duration: 12 },
  { id: 'enneagram', name: t.tests.enneagram.name, subtitle: t.tests.enneagram.description, icon: '/picto-enneagram.png', color: '#EC4899', duration: 10 },
];

// Progress bar component
const ProgressBar = ({ value, max = 100, color = '#F97316', height = 8 }) => {
  const percentage = Math.round((value / max) * 100);
  return (
    <div className="w-full bg-gray-700 rounded-full overflow-hidden" style={{ height }}>
      <div 
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${percentage}%`, backgroundColor: color }}
      />
    </div>
  );
};

// Schwartz SVG Icon Component
const SchwartzIcon = () => (
  <svg viewBox="0 0 64 64" className="w-10 h-10">
    <circle cx="32" cy="32" r="26" fill="none" stroke="#F59E0B" strokeWidth="2" opacity="0.3"/>
    <path d="M32 8 A24 24 0 0 1 56 32" fill="none" stroke="#EF4444" strokeWidth="4" strokeLinecap="round"/>
    <path d="M56 32 A24 24 0 0 1 32 56" fill="none" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round"/>
    <path d="M32 56 A24 24 0 0 1 8 32" fill="none" stroke="#22C55E" strokeWidth="4" strokeLinecap="round"/>
    <path d="M8 32 A24 24 0 0 1 32 8" fill="none" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="32" cy="32" r="10" fill="#F59E0B" opacity="0.2"/>
  </svg>
);

// Test Icon Component
const TestIcon = ({ icon }) => {
  if (icon === 'schwartz') {
    return <SchwartzIcon />;
  }
  return <img src={icon} alt="" className="w-10 h-10 object-contain" />;
};

// Test Card Component
const TestCard = ({ test, status, onStart, onViewResults, onUpsell, upgradeCtaLabel }) => {
  const { t } = useTranslation();
  const isCompleted = status === 'completed';
  const isInProgress = status === 'in_progress';
  const isLocked = status === 'locked';

  if (isLocked) {
    return (
      <div
        className="bg-[#1F2937] rounded-xl p-5 border border-gray-800 relative overflow-hidden grayscale opacity-95"
        data-testid={`test-locked-${test.id}`}
      >
        {/* Dim overlay */}
        <div className="absolute inset-0 bg-black/50 pointer-events-none" aria-hidden="true" />
        {/* Lock icon top-right */}
        <div className="absolute top-3 right-3 z-10 w-8 h-8 bg-gray-900/90 rounded-full flex items-center justify-center border border-gray-700">
          <Lock className="w-4 h-4 text-gray-300" />
        </div>

        <div className="relative z-0 flex items-center gap-3 mb-4 opacity-60">
          <TestIcon icon={test.icon} />
          <div>
            <h3 className="font-bold text-white">{test.name}</h3>
            <p className="text-gray-500 text-sm">{test.subtitle}</p>
          </div>
        </div>

        <div className="relative z-10 mt-2 space-y-3 border-t border-gray-700/60 pt-3">
          <p className="text-xs text-orange-300/90 font-medium">{t.dashboard.upgradePrompt}</p>
          <button
            onClick={() => onUpsell(test.id)}
            data-testid={`test-upgrade-${test.id}`}
            className="w-full px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {upgradeCtaLabel || t.dashboard.upgradeCTA}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[#1F2937] rounded-xl p-5 border transition-all ${
      isCompleted 
        ? 'border-green-500/30' 
        : isInProgress 
          ? 'border-orange-500/50 animate-pulse' 
          : 'border-gray-700 hover:border-gray-600'
    }`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <TestIcon icon={test.icon} />
          <div>
            <h3 className="font-bold text-white">{test.name}</h3>
            <p className="text-gray-500 text-sm">{test.subtitle}</p>
          </div>
        </div>
        
        {isCompleted ? (
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-white" />
          </div>
        ) : isInProgress ? (
          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center animate-pulse">
            <Clock className="w-4 h-4 text-white" />
          </div>
        ) : (
          <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
            <Circle className="w-5 h-5 text-gray-500" />
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-gray-500 text-sm flex items-center gap-1">
          <Clock className="w-4 h-4" />
          {test.duration} min
        </span>
        
        {isCompleted ? (
          <button
            onClick={() => onViewResults(test.id)}
            className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            {t.dashboard.viewResults}
          </button>
        ) : (
          <button
            onClick={() => onStart(test.id)}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            {isInProgress ? t.dashboard.resumeTest : t.dashboard.startTest}
          </button>
        )}
      </div>
    </div>
  );
};

// AI Synthesis Card (Locked or Unlocked)
const AISynthesisCard = ({ unlocked, synthesis, onGenerate, loading }) => {
  const { t } = useTranslation();
  if (!unlocked) {
    return (
      <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/80 z-10" />
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="text-center">
            <Lock className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2" data-testid="synthesis-locked-title">{t.dashboard.synthesisLocked}</h3>
            <p className="text-gray-400" data-testid="synthesis-locked-desc">{t.dashboard.synthesisLockedDesc}</p>
          </div>
        </div>
        <div className="opacity-20">
          <h3 className="text-xl font-bold text-orange-500 mb-4">{t.dashboard.synthesisTitle}</h3>
          <div className="h-32 bg-gray-800 rounded-lg mb-4" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-20 bg-gray-800 rounded-lg" />
            <div className="h-20 bg-gray-800 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }
  
  if (!synthesis) {
    return (
      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6" />
            {t.dashboard.synthesisTitle}
          </h3>
          <span className="px-3 py-1 bg-white/20 rounded-full text-sm">{t.dashboard.synthesisAvailable}</span>
        </div>
        <p className="text-white/80 mb-6">{t.dashboard.synthesisReadyDesc}</p>
        <button
          onClick={onGenerate}
          disabled={loading}
          data-testid="generate-synthesis-btn"
          className="w-full py-3 bg-white text-orange-600 font-semibold rounded-xl hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              {t.dashboard.generating}
            </>
          ) : (
            <>
              <Zap className="w-5 h-5" />
              {t.dashboard.generateSynthesis}
            </>
          )}
        </button>
      </div>
    );
  }

  // Full synthesis display
  const portrait = synthesis.portrait || {};
  const forces = synthesis.forces || {};
  const style = synthesis.style_communication || {};
  const motivations = synthesis.motivations || {};
  const plan = synthesis.plan_developpement || {};
  const carrieres = synthesis.recommandations_carrieres || {};
  const relations = synthesis.relations || {};
  const insight = synthesis.insight_unique || {};

  return (
    <div className="space-y-4" data-testid="synthesis-display">
      {/* Header - Portrait */}
      <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-10 translate-x-10" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-10 -translate-x-10" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-4xl">{portrait.emoji || '🎯'}</span>
            <div>
              <p className="text-white/60 text-sm uppercase tracking-wider">Ton profil</p>
              <h2 className="text-2xl sm:text-3xl font-bold">{portrait.titre || 'Votre Profil'}</h2>
            </div>
          </div>
          {synthesis.situation_label && (
            <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm mt-2 mb-4">
              {synthesis.situation_label}
            </span>
          )}
          <p className="text-white/90 leading-relaxed text-base">{portrait.resume}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Forces */}
        <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700/50">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-yellow-400" />
            </div>
            {forces.titre_section || 'Tes super-pouvoirs'}
          </h3>
          <div className="space-y-3">
            {(forces.items || []).map((item, i) => (
              <div key={i} className="bg-[#111827] rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-yellow-400 text-xs font-bold">{i + 1}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">{item.force}</p>
                    <p className="text-gray-400 text-xs mt-1 leading-relaxed">{item.description}</p>
                    {item.tests_source && (
                      <span className="text-[10px] text-orange-400/60 mt-1 inline-block">{item.tests_source}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Communication Style */}
        <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700/50">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-blue-400" />
            </div>
            {style.titre_section || 'Ton style de communication'}
          </h3>
          <p className="text-gray-300 text-sm leading-relaxed mb-4">{style.description}</p>
          <div className="space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-green-400 mb-2 font-semibold">Points forts</p>
              <div className="flex flex-wrap gap-2">
                {(style.points_forts || []).map((p, i) => (
                  <span key={i} className="px-2 py-1 bg-green-500/10 text-green-400 rounded-lg text-xs border border-green-500/20">{p}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-amber-400 mb-2 font-semibold">Points de vigilance</p>
              <div className="flex flex-wrap gap-2">
                {(style.points_vigilance || []).map((p, i) => (
                  <span key={i} className="px-2 py-1 bg-amber-500/10 text-amber-400 rounded-lg text-xs border border-amber-500/20">{p}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Motivations */}
      <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700/50">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center">
            <Heart className="w-4 h-4 text-rose-400" />
          </div>
          {motivations.titre_section || 'Ce qui te drive'}
        </h3>
        <p className="text-gray-300 text-sm leading-relaxed mb-4">{motivations.description}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-rose-400 mb-2 font-semibold">Tes moteurs</p>
            <ul className="space-y-2">
              {(motivations.moteurs || []).map((m, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                  <Rocket className="w-3 h-3 text-rose-400 flex-shrink-0" /> {m}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-purple-400 mb-2 font-semibold">Valeurs clés</p>
            <div className="flex flex-wrap gap-2">
              {(motivations.valeurs_cles || []).map((v, i) => (
                <span key={i} className="px-3 py-1.5 bg-purple-500/10 text-purple-300 rounded-lg text-sm border border-purple-500/20">{v}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Development Plan - The star section */}
      <div className="bg-gradient-to-br from-[#1a2332] to-[#1F2937] rounded-2xl p-6 sm:p-8 border border-orange-500/30">
        <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <Target className="w-4 h-4 text-orange-400" />
          </div>
          {plan.titre_section || 'Ton plan de développement personnalisé'}
        </h3>
        <p className="text-gray-400 text-sm mb-6">{plan.intro}</p>
        <div className="space-y-4">
          {(plan.axes || []).map((axe, i) => (
            <div key={i} className="bg-[#111827] rounded-xl p-4 border-l-4 border-orange-500">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</span>
                <h4 className="font-bold text-white">{axe.axe}</h4>
              </div>
              <p className="text-gray-400 text-sm mb-3 ml-9">{axe.pourquoi}</p>
              <div className="ml-9 space-y-1.5 mb-3">
                {(axe.actions || []).map((action, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm text-gray-300">
                    <ArrowRight className="w-3 h-3 text-orange-400 flex-shrink-0" />
                    <span>{action}</span>
                  </div>
                ))}
              </div>
              {axe.objectif && (
                <div className="ml-9 px-3 py-2 bg-orange-500/10 rounded-lg border border-orange-500/20">
                  <p className="text-orange-300 text-xs"><span className="font-semibold">Objectif :</span> {axe.objectif}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Career Recommendations */}
        <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700/50">
          <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-emerald-400" />
            </div>
            {carrieres.titre_section || 'Pistes professionnelles'}
          </h3>
          <p className="text-gray-400 text-xs mb-4">{carrieres.intro}</p>
          <div className="space-y-2">
            {(carrieres.pistes || []).map((p, i) => (
              <div key={i} className="bg-[#111827] rounded-lg p-3">
                <p className="font-semibold text-emerald-400 text-sm">{p.domaine}</p>
                <p className="text-gray-400 text-xs mt-1">{p.pourquoi}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Professional Relations */}
        <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700/50">
          <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Users className="w-4 h-4 text-cyan-400" />
            </div>
            {relations.titre_section || 'Tes relations professionnelles'}
          </h3>
          <p className="text-gray-300 text-sm leading-relaxed mb-4">{relations.style_collaboration}</p>
          <div className="mb-4">
            <p className="text-xs uppercase tracking-wider text-cyan-400 mb-2 font-semibold">Profils complémentaires</p>
            <ul className="space-y-1.5">
              {(relations.profils_complementaires || []).map((p, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                  <Users className="w-3 h-3 text-cyan-400 flex-shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </div>
          {relations.conseil_cle && (
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3">
              <p className="text-cyan-300 text-sm"><Lightbulb className="w-3 h-3 inline mr-1" />{relations.conseil_cle}</p>
            </div>
          )}
        </div>
      </div>

      {/* Unique Insight */}
      {insight.texte && (
        <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 rounded-2xl p-6 border border-purple-500/20 mt-6">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-purple-400" />
            </div>
            {insight.titre || 'Le savais-tu ?'}
          </h3>
          <p className="text-gray-300 text-sm leading-relaxed italic">{insight.texte}</p>
        </div>
      )}

      {/* Regenerate button */}
      <div className="text-center">
        <button
          onClick={onGenerate}
          disabled={loading}
          data-testid="regenerate-synthesis-btn"
          className="px-6 py-2 bg-[#1F2937] text-gray-400 hover:text-white border border-gray-700 rounded-xl text-sm transition-colors inline-flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? t.dashboard.regenerating : t.dashboard.regenerate}
        </button>
      </div>
    </div>
  );
};

// Group Slot Card Components
// Group Slot Card
const GroupSlotCard = ({ slot, onInvite, onReassign, onViewProfile }) => {
  const { t } = useTranslation();
  const statut = slot.statut;
  const member = slot.member;
  
  if (statut === 'disponible' && !member) {
    return (
      <div className="bg-[#111827] rounded-xl p-4 border border-dashed border-gray-600" data-testid={`slot-${slot.slot_number}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
            <User className="w-5 h-5 text-gray-500" />
          </div>
          <div className="flex-1">
            <p className="text-gray-400 font-medium">{t.dashboard.slotAvailable}</p>
          </div>
          <button onClick={() => onInvite(slot.slot_id)} className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1" data-testid={`invite-btn-${slot.slot_number}`}>
            <span>+</span> {t.dashboard.sendInvite}
          </button>
        </div>
      </div>
    );
  }
  
  if (statut === 'invite_envoye' && !member) {
    return (
      <div className="bg-[#111827] rounded-xl p-4 border border-gray-700" data-testid={`slot-${slot.slot_number}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="text-white font-medium">{slot.email_invite}</p>
            <p className="text-blue-400 text-sm">{t.dashboard.inviteSent}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onReassign(slot.slot_id, slot.email_invite)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> {t.dashboard.resend}
            </button>
            <button onClick={() => onInvite(slot.slot_id)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors">
              {t.dashboard.reassign}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (member) {
    const isComplete = member.completed_tests >= 6;
    const canView = isComplete && member.share_results;
    const isPrivate = isComplete && !member.share_results;
    const isOwnerSlot = member.group_role === 'owner';
    
    return (
      <div className="bg-[#111827] rounded-xl p-4 border border-gray-700" data-testid={`slot-${slot.slot_number}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
            <User className={`w-5 h-5 ${isComplete ? 'text-green-400' : 'text-yellow-400'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-white font-medium">
                {member.prenom} {member.nom?.charAt(0)}.
                {isOwnerSlot && <span className="text-orange-400 text-xs ml-1">{t.dashboard.you}</span>}
              </p>
              <span className="text-xs text-gray-500">{member.completed_tests}/6 tests</span>
            </div>
            <p className="text-gray-400 text-sm">{member.situation_label}</p>
            {isPrivate && (
              <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                <Lock className="w-3 h-3" /> {t.dashboard.privateResults}
              </p>
            )}
          </div>
          {canView && !isOwnerSlot && (
            <button onClick={() => onViewProfile(member.user_id)} className="px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-1" data-testid={`view-profile-${slot.slot_number}`}>
              <Eye className="w-4 h-4" /> {t.dashboard.viewProfile}
            </button>
          )}
        </div>
      </div>
    );
  }
  
  return null;
};

// Share Results Toggle (for members only)
const ShareResultsToggle = ({ shareResults, ownerInfo, onToggle }) => {
  const { t } = useTranslation();
  if (!ownerInfo) return null;
  
  return (
    <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700" data-testid="share-toggle">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
          {shareResults ? <Shield className="w-5 h-5 text-green-400" /> : <Lock className="w-5 h-5 text-gray-400" />}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-white mb-1">{t.dashboard.shareTitle}</h3>
          <p className="text-gray-400 text-sm mb-4">
            {t.dashboard.shareQuestion} <span className="text-white font-medium">{ownerInfo.prenom} {ownerInfo.nom}</span> {t.dashboard.shareQuestionEnd}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => onToggle(false)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !shareResults 
                  ? 'bg-gray-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid="share-no-btn"
            >
              {t.dashboard.shareNo}
            </button>
            <button
              onClick={() => onToggle(true)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                shareResults 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid="share-yes-btn"
            >
              {t.dashboard.shareYes}
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-3">{t.dashboard.shareChangeAnytime}</p>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
export default function UserDashboard() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { currency, format } = useCurrency();
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [synthesisLoading, setSynthesisLoading] = useState(false);
  const [synthesis, setSynthesis] = useState(null);
  const [inviteModalSlot, setInviteModalSlot] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [duoStatus, setDuoStatus] = useState(null);
  const [partnerEmailInput, setPartnerEmailInput] = useState('');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  
  // Fetch dashboard data and assessment
  const fetchDashboardData = async () => {
    const token = localStorage.getItem('bpm_access_token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      // Get or create assessment
      const assessResponse = await fetch(`${API}/api/user/assessment`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (assessResponse.status === 401) {
        localStorage.removeItem('bpm_access_token');
        navigate('/login');
        return;
      }
      
      if (assessResponse.ok) {
        const assessData = await assessResponse.json();
        setAssessmentId(assessData.assessment_id);
        localStorage.setItem('bpm_assessment_id', assessData.assessment_id);
      }
      
      // Get dashboard data
      const response = await fetch(`${API}/api/user/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erreur de chargement');
      
      const data = await response.json();
      setUser(data.user);
      setDashboardData(data);
      
      if (data.ai_synthesis) {
        setSynthesis(data.ai_synthesis);
      }
      
      // Check for duo status if user has duo pack
      if (data.user?.pack === 'duo' && data.user?.duo_link_id) {
        try {
          const duoRes = await fetch(`${API}/api/duo/status/${data.user.duo_link_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (duoRes.ok) {
            const duoData = await duoRes.json();
            setDuoStatus(duoData);
          }
        } catch (e) {
          console.error('Duo status error:', e);
        }
      }
      
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDashboardData();
  }, [navigate]);
  
  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('bpm_access_token');
    localStorage.removeItem('bpm_refresh_token');
    localStorage.removeItem('bpm_user');
    localStorage.removeItem('bpm_assessment_id');
    navigate('/login');
  };
  
  // Start test handler - go directly to the test
  const handleStartTest = (testId) => {
    if (assessmentId) {
      navigate(`/assessment/${assessmentId}?tests=${testId}`);
    }
  };
  
  // View all tests - go to selection page
  const handleViewAllTests = () => {
    if (assessmentId) {
      navigate(`/test-selection/${assessmentId}`);
    }
  };
  
  // View results handler
  const handleViewResults = (testId) => {
    if (assessmentId) {
      navigate(`/results/${assessmentId}`);
    }
  };
  
  // Generate AI synthesis
  const handleGenerateSynthesis = async () => {
    setSynthesisLoading(true);
    
    try {
      const token = localStorage.getItem('bpm_access_token');
      const response = await fetch(`${API}/api/user/ai-synthesis?lang=${language}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        const err = await response.json();
        alert(err.detail || t.dashboard.synthesisComplete6);
        return;
      }
      
      if (!response.ok) throw new Error('Generation error');
      
      const data = await response.json();
      setSynthesis(data.synthesis);
      
    } catch (err) {
      alert(t.dashboard.synthesisError);
    } finally {
      setSynthesisLoading(false);
    }
  };
  
  // Group management handlers
  const handleInvite = async (slotId, emailOverride) => {
    if (emailOverride) {
      // This is a "relancer" (resend), reuse same email
      setInviteLoading(true);
      try {
        const token = localStorage.getItem('bpm_access_token');
        const orderId = dashboardData?.group_data?.order_id;
        const response = await fetch(`${API}/api/group/${orderId}/reassign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ slot_id: slotId, nouvel_email: emailOverride })
        });
        if (!response.ok) { const e = await response.json(); alert(e.detail); return; }
        alert('Invitation renvoyée !');
        fetchDashboardData();
      } catch { alert('Erreur lors du renvoi'); }
      finally { setInviteLoading(false); }
      return;
    }
    setInviteModalSlot(slotId);
    setInviteEmail('');
  };
  
  const handleSendInvite = async () => {
    if (!inviteEmail) return;
    setInviteLoading(true);
    try {
      const token = localStorage.getItem('bpm_access_token');
      const orderId = dashboardData?.group_data?.order_id;
      
      // Check if this is a reassign (slot already has invite) or new invite
      const slot = dashboardData?.group_data?.slots?.find(s => s.slot_id === inviteModalSlot);
      const isReassign = slot?.statut === 'invite_envoye';
      
      const url = isReassign
        ? `${API}/api/group/${orderId}/reassign`
        : `${API}/api/group/${orderId}/invite`;
      
      const body = isReassign
        ? { slot_id: inviteModalSlot, nouvel_email: inviteEmail }
        : { slot_id: inviteModalSlot, email_invite: inviteEmail };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) { const e = await response.json(); alert(e.detail); return; }
      
      alert(`${t.dashboard.inviteSentTo} ${inviteEmail}`);
      setInviteModalSlot(null);
      fetchDashboardData();
    } catch { alert(t.common.error); }
    finally { setInviteLoading(false); }
  };
  
  const handleViewMemberProfile = (userId) => {
    window.open(`/member-profile/${userId}`, '_blank');
  };
  
  const handleToggleShare = async (value) => {
    try {
      const token = localStorage.getItem('bpm_access_token');
      const response = await fetch(`${API}/api/user/share-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ share_results: value })
      });
      if (response.ok) {
        setDashboardData(prev => ({
          ...prev,
          user: { ...prev.user, share_results: value }
        }));
      }
    } catch { alert('Erreur lors de la mise à jour'); }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">{t.dashboard.loadingDashboard}</p>
        </div>
      </div>
    );
  }
  
  const completedTests = dashboardData?.completed_tests || [];
  const progress = completedTests.length;
  const situationColor = SITUATION_COLORS[user?.situation] || 'bg-green-500';
  const situationLabel = t.register.situations[user?.situation] || user?.situation || '';
  const testsConfig = getTestsConfig(t);

  // Phase 2, pack-based test access (source of truth = backend)
  const testsAuthorized = dashboardData?.tests_authorized || null;
  const accessExpiresAt = dashboardData?.access_expires_at || null;
  const formattedExpiry = accessExpiresAt
    ? new Date(accessExpiresAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const computeStatus = (testId) => {
    if (completedTests.includes(testId)) return 'completed';
    // null tests_authorized → show all as pending (backend hasn't propagated yet or legacy user)
    if (testsAuthorized && !testsAuthorized.includes(testId)) return 'locked';
    return 'pending';
  };

  const handleUpsell = async (testId) => {
    // PostHog tracking BEFORE the API call (privacy-safe)
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture('upgrade_started', {
          upgrade_type: 'decouverte_to_complete',
          from_pack: 'decouverte',
          to_pack: 'complete',
          price: dashboardData?.upgrade_price_eur,
          currency: currency,
          source: 'locked_test_card',
          test_id: testId,
        });
      }
    } catch (e) { /* ignore */ }

    try {
      const token = localStorage.getItem('bpm_access_token');
      const res = await fetch(`${API}/api/checkout/upgrade-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          upgrade_type: 'decouverte_to_complete',
          currency: currency,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // eslint-disable-next-line no-alert
        alert(data.detail || t.upgrade.errorGeneric);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(t.upgrade.errorGeneric);
    }
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleCompleteToDuoUpgrade = async () => {
    const email = (partnerEmailInput || '').trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      // eslint-disable-next-line no-alert
      alert(t.upgrade.invalidPartnerEmail);
      return;
    }
    const myEmail = (user?.email || '').toLowerCase();
    if (myEmail && email === myEmail) {
      // eslint-disable-next-line no-alert
      alert(t.upgrade.partnerSameAsUser);
      return;
    }

    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture('upgrade_started', {
          upgrade_type: 'complete_to_duo',
          from_pack: 'complete',
          to_pack: 'duo',
          price: dashboardData?.upgrade_price_eur,
          currency: currency,
          source: 'complete_to_duo_banner',
          partner_email_domain: email.split('@')[1] || null,
        });
      }
    } catch (e) { /* ignore */ }

    setUpgradeLoading(true);
    try {
      const token = localStorage.getItem('bpm_access_token');
      const res = await fetch(`${API}/api/checkout/upgrade-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          upgrade_type: 'complete_to_duo',
          currency: currency,
          partner_email: email,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // eslint-disable-next-line no-alert
        alert(data.detail || t.upgrade.errorGeneric);
        setUpgradeLoading(false);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      } else {
        setUpgradeLoading(false);
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(t.upgrade.errorGeneric);
      setUpgradeLoading(false);
    }
  };


  
  return (
    <div className="min-h-screen bg-[#111827]">
      {/* Header */}
      <header className="bg-[#1F2937] border-b border-gray-700 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold">
                <span className="text-orange-500">BPM</span>
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <div className="hidden sm:block text-right">
                <p className="text-white font-medium">{t.dashboard.hello} {user?.prenom || 'User'} 👋</p>
                {user?.situation && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${situationColor} text-white`}>
                    {situationLabel}
                  </span>
                )}
              </div>
              
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Progress Section */}
        <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white">{t.dashboard.progress}</h2>
              <p className="text-gray-400">{progress}/6 {t.dashboard.testsCompleted}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 relative">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="40" cy="40" r="35" fill="none" stroke="#374151" strokeWidth="6"/>
                  <circle 
                    cx="40" cy="40" r="35" fill="none" stroke="#F97316" strokeWidth="6"
                    strokeDasharray={`${(progress/6)*220} 220`}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-white">
                  {Math.round((progress/6)*100)}%
                </span>
              </div>
            </div>
          </div>
          <ProgressBar value={progress} max={6} />
        </div>
        
        {/* Access expires banner */}
        {formattedExpiry && (
          <div
            className="bg-[#1F2937] border border-gray-700 rounded-xl px-4 py-3 mb-6 text-sm text-gray-400 flex items-center gap-2"
            data-testid="access-expires"
          >
            <Clock className="w-4 h-4 text-orange-400 flex-shrink-0" />
            <span>{t.dashboard.accessExpiresOn} <span className="text-white font-medium">{formattedExpiry}</span></span>
          </div>
        )}

        {/* Complete → Duo upgrade banner */}
        {dashboardData?.upgrade_type === 'complete_to_duo' && (
          <div
            className="bg-gradient-to-r from-orange-500/10 to-purple-500/10 border border-orange-500/30 rounded-xl p-6 mb-6"
            data-testid="complete-to-duo-banner"
          >
            <div className="flex items-center gap-3 mb-2">
              <Heart className="w-6 h-6 text-orange-400 flex-shrink-0" />
              <h3 className="text-xl font-bold text-white">{t.dashboard.upgradeFromComplete.title}</h3>
            </div>
            <p className="text-gray-300 mb-4 text-sm sm:text-base">
              {t.dashboard.upgradeFromComplete.subtitle}
            </p>
            <ul className="space-y-1.5 mb-5">
              {[1, 2, 3].map(i => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>{t.dashboard.upgradeFromComplete[`feature${i}`]}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <input
                type="email"
                value={partnerEmailInput}
                onChange={(e) => setPartnerEmailInput(e.target.value)}
                placeholder={t.dashboard.upgradeFromComplete.partnerEmailPlaceholder}
                className="flex-1 px-4 py-3 bg-[#111827] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                data-testid="partner-email-input"
                disabled={upgradeLoading}
              />
              <button
                onClick={handleCompleteToDuoUpgrade}
                disabled={upgradeLoading}
                data-testid="upgrade-to-duo-cta"
                className="px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {upgradeLoading
                  ? t.upgrade.processing
                  : t.dashboard.upgradeFromComplete.cta.replace('{price}', format(dashboardData.upgrade_price_eur))}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              {t.dashboard.upgradeFromComplete.disclaimer
                .replace('{price}', format(dashboardData.upgrade_price_eur))
                .replace('{fullPrice}', format(39.99))}
            </p>
          </div>
        )}

        {/* Tests Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{t.dashboard.dimensions}</h2>
            <button
              onClick={handleViewAllTests}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2 text-sm"
            >
              {t.dashboard.selectMultiple}
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {testsConfig.map(test => (
              <TestCard
                key={test.id}
                test={test}
                status={computeStatus(test.id)}
                onStart={handleStartTest}
                onViewResults={handleViewResults}
                onUpsell={handleUpsell}
                upgradeCtaLabel={
                  dashboardData?.upgrade_price_eur != null
                    ? t.dashboard.upgradeFromDiscovery.replace('{price}', format(dashboardData.upgrade_price_eur))
                    : undefined
                }
              />
            ))}
          </div>
        </div>
        
        {/* AI Synthesis */}
        <div className="mb-8">
          <AISynthesisCard 
            unlocked={progress >= 6}
            synthesis={synthesis}
            onGenerate={handleGenerateSynthesis}
            loading={synthesisLoading}
          />
        </div>
        
        {/* Duo Compatibility Section */}
        {user?.pack === 'duo' && (
          <div className="mb-8" data-testid="duo-section">
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-2xl p-6 border border-purple-500/30">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Users className="w-6 h-6 text-purple-400" />
                  {t.duo.yourDuo}
                </h3>
                {duoStatus?.report_ready && (
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">{t.duo.reportReady}</span>
                )}
              </div>
              
              {duoStatus?.report_ready ? (
                <div>
                  <p className="text-gray-400 mb-4">{t.duo.reportReadyDesc}</p>
                  <button
                    onClick={() => navigate(`/duo/${user.duo_link_id}`)}
                    className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
                    data-testid="duo-view-report-btn"
                  >
                    <Sparkles className="w-5 h-5" />
                    {t.duo.viewReport}
                  </button>
                </div>
              ) : duoStatus?.partner_joined ? (
                <div>
                  <p className="text-gray-400 mb-2">{t.duo.partner} : <span className="text-white font-medium">{duoStatus.partner_name}</span></p>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-gray-500 text-sm">{t.duo.partnerProgress}</span>
                    <span className="text-purple-400 font-bold">{duoStatus.partner_tests || 0}/6</span>
                  </div>
                  {progress >= 3 && (duoStatus.partner_tests || 0) >= 3 ? (
                    <button
                      onClick={() => navigate(`/duo/${user.duo_link_id}`)}
                      className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
                      data-testid="duo-generate-btn"
                    >
                      <Sparkles className="w-5 h-5" />
                      {t.duo.generateReport}
                    </button>
                  ) : (
                    <p className="text-gray-500 text-sm">{t.duo.testsNeeded}</p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-gray-400 mb-2">{t.duo.waitingPartner}</p>
                  <p className="text-gray-500 text-sm">{t.duo.waitingPartnerDesc}</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Team Section, Owner only for Trio/Groupe */}
        {dashboardData?.group_data && (
          <div className="mb-8" data-testid="group-section">
            <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Users className="w-6 h-6 text-orange-500" />
                  {t.dashboard.myTeam}
                </h3>
                <span className="text-gray-400 text-sm">
                  {dashboardData.group_data.slots?.filter(s => s.member).length}/{dashboardData.group_data.slots?.length} {t.dashboard.members}
                </span>
              </div>
              <div className="space-y-3">
                {dashboardData.group_data.slots?.map((slot) => (
                  <GroupSlotCard
                    key={slot.slot_id || slot.slot_number}
                    slot={slot}
                    onInvite={handleInvite}
                    onReassign={(slotId, email) => handleInvite(slotId, email)}
                    onViewProfile={handleViewMemberProfile}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Share Results Toggle, Members only */}
        {user?.group_role === 'member' && user?.pack && user.pack !== 'solo' && dashboardData?.owner_info && (
          <div className="mb-8">
            <ShareResultsToggle
              shareResults={dashboardData?.user?.share_results || false}
              ownerInfo={dashboardData.owner_info}
              onToggle={handleToggleShare}
            />
          </div>
        )}
        
        {/* Invite Modal */}
        {inviteModalSlot && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setInviteModalSlot(null)}>
            <div className="bg-[#1F2937] rounded-2xl p-6 w-full max-w-md border border-gray-700" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white mb-4">{t.dashboard.inviteMember}</h3>
              <input
                type="email"
                placeholder={t.dashboard.emailAddress}
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white mb-4 focus:outline-none focus:border-orange-500"
                data-testid="invite-email-input"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setInviteModalSlot(null)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {t.common.cancel}
                </button>
                <button
                  onClick={handleSendInvite}
                  disabled={inviteLoading || !inviteEmail}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                  data-testid="send-invite-btn"
                >
                  {inviteLoading ? t.dashboard.sending : t.dashboard.sendInviteBtn}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
